/* Monticule homepage */
(() => {
  if (customElements.get("monticule-homepage")) return;

  class MonticuleHomepage extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;

      this.controller = new AbortController();

      const signal = this.controller.signal;

      const on = (target, event, handler) => {
        target?.addEventListener(event, handler, { signal });
      };

      const q = (selector) => this.querySelector(selector);
      const qa = (selector) => [...this.querySelectorAll(selector)];

      /* MOBILE MENU */

      const toggle = q(".menu");
      const menu = q(".mth-mobile-nav");
      const floatingMenu = q(".floating-menu");

      const closeMenu = () => {
        if (!menu || !toggle) return;

        menu.hidden = true;
        toggle.setAttribute("aria-expanded", "false");
        toggle.textContent = "Menu";
      };

      on(toggle, "click", () => {
        if (!menu) return;

        const open = toggle.getAttribute("aria-expanded") === "true";

        menu.hidden = open;
        toggle.setAttribute("aria-expanded", String(!open));
        toggle.textContent = open ? "Menu" : "Close";

        this.refreshCart();
      });

      on(menu, "click", (event) => {
        if (event.target.closest("a")) closeMenu();
      });

      on(document, "click", (event) => {
        if (
          menu &&
          !menu.hidden &&
          floatingMenu &&
          !floatingMenu.contains(event.target)
        ) {
          closeMenu();
        }
      });

      on(document, "keydown", (event) => {
        if (event.key === "Escape" && menu && !menu.hidden) {
          closeMenu();
          toggle?.focus();
        }
      });

      /* PRODUCT FILTERS */

      const filters = qa("[data-filter]");
      const products = qa("[data-category]");
      const filterStatus = q("[data-filter-status]");

      filters.forEach((button) => {
        const category = button.dataset.filter;

        if (
          category !== "all" &&
          !products.some((product) => product.dataset.category === category)
        ) {
          button.hidden = true;
        }

        on(button, "click", () => {
          filters.forEach((filter) => {
            const active = filter === button;

            filter.classList.toggle("active", active);
            filter.setAttribute("aria-pressed", String(active));
          });

          let count = 0;

          products.forEach((product) => {
            product.hidden =
              category !== "all" &&
              product.dataset.category !== category;

            if (!product.hidden) count++;
          });

          if (filterStatus) {
            filterStatus.textContent = `Showing ${count} products`;
          }
        });
      });

      /* TEAM CAROUSEL */

      const track = q(".team-track");
      const previous = q(".team-prev");
      const next = q(".team-next");

      const updateControls = () => {
        if (!track) return;

        if (previous) {
          previous.disabled = track.scrollLeft <= 2;
        }

        if (next) {
          next.disabled =
            track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
        }
      };

      const move = (direction) => {
        const card = track?.querySelector(".rider-card");
        if (!card) return;

        const reducedMotion = window.matchMedia(
          "(prefers-reduced-motion: reduce)"
        ).matches;

        track.scrollBy({
          left: direction * (card.getBoundingClientRect().width + 10),
          behavior: reducedMotion ? "auto" : "smooth"
        });
      };

      on(previous, "click", () => move(-1));
      on(next, "click", () => move(1));
      on(track, "scroll", updateControls);
      on(window, "resize", updateControls);

      on(track, "keydown", (event) => {
        if (
          event.target === track &&
          ["ArrowLeft", "ArrowRight"].includes(event.key)
        ) {
          event.preventDefault();
          move(event.key === "ArrowRight" ? 1 : -1);
        }
      });

      updateControls();

      /* RIDER POPUPS */

      const dialog = q(".rider-dialog");
      let opener;

      const openRider = (card) => {
        if (!dialog || typeof dialog.showModal !== "function") {
          return false;
        }

        opener = card;

        const name = q("[data-rider-name]");
        const location = q("[data-rider-location]");
        const discipline = q("[data-rider-discipline]");
        const photo = q("[data-rider-image]");
        const bio = q("[data-rider-bio]");
        const signature = q("[data-rider-signature]");
        const source = card.querySelector("img");

        if (name) {
          name.textContent = card.dataset.name || "";
        }

        if (location) {
          location.textContent = card.dataset.location || "";
        }

        if (discipline) {
          discipline.textContent =
            "MONTICULE TEAM / " + (card.dataset.discipline || "");
        }

        if (photo) {
          photo.hidden = !source;

          if (source) {
            photo.src = source.currentSrc || source.src;
            photo.alt = card.dataset.name || "";
          } else {
            photo.removeAttribute("src");
          }
        }

        if (bio) {
          bio.textContent = card.dataset.bio || "";
          bio.hidden = !card.dataset.bio;
        }

        if (signature) {
          signature.hidden = !card.dataset.signatureUrl;
          signature.removeAttribute("href");

          if (card.dataset.signatureUrl) {
            signature.href = card.dataset.signatureUrl;
            signature.textContent =
              "Shop " + (card.dataset.signatureName || "signature product");
          }
        }

        dialog.showModal();
        return true;
      };

      qa("[data-rider]").forEach((card) => {
        on(card, "click", (event) => {
          if (
            event.metaKey ||
            event.ctrlKey ||
            event.shiftKey ||
            event.altKey
          ) {
            return;
          }

          if (openRider(card)) {
            event.preventDefault();
          }
        });
      });

      on(q(".rider-close"), "click", () => {
        dialog?.close();
      });

      on(dialog, "click", (event) => {
        if (event.target !== dialog) return;

        const bounds = dialog.getBoundingClientRect();

        if (
          event.clientX < bounds.left ||
          event.clientX > bounds.right ||
          event.clientY < bounds.top ||
          event.clientY > bounds.bottom
        ) {
          dialog.close();
        }
      });

      on(dialog, "close", () => {
        opener?.focus();
      });

      /* SHOPIFY THEME EDITOR */

      on(document, "shopify:block:select", (event) => {
        if (!this.contains(event.target)) return;

        const rider = event.target.closest("[data-rider]");

        if (rider) {
          rider.scrollIntoView({
            behavior: "auto",
            block: "nearest",
            inline: "center"
          });
        }
      });

      /* LIVE CART COUNT */

      on(window, "focus", () => this.refreshCart());
      on(window, "pageshow", () => this.refreshCart());

      on(document, "visibilitychange", () => {
        if (!document.hidden) this.refreshCart();
      });

      [
        "cart:updated",
        "cart:change",
        "cart:refresh",
        "cart:update",
        "product:added"
      ].forEach((eventName) => {
        on(document, eventName, () => this.refreshCart());
      });

      if (q("[data-cart-count]")) {
        this.refreshCart();

        this.cartTimer = setInterval(() => {
          if (!document.hidden) this.refreshCart();
        }, 15000);
      }
    }

    async refreshCart() {
      if (
        !this.controller ||
        this.cartPending ||
        !this.querySelector("[data-cart-count]")
      ) {
        return;
      }

      this.cartPending = true;

      try {
        const url =
          this.dataset.cartUrl ||
          (window.Shopify?.routes?.root || "/") + "cart.js";

        const response = await fetch(url, {
          credentials: "same-origin",
          cache: "no-store",
          signal: this.controller.signal
        });

        if (!response.ok) return;

        const cart = await response.json();

        if (!Number.isInteger(cart.item_count)) return;

        this.querySelectorAll("[data-cart-count]").forEach((element) => {
          element.textContent = cart.item_count
            ? ` (${cart.item_count})`
            : "";
        });

        const mobileCart = this.querySelector("[data-mobile-cart]");

        if (mobileCart) {
          mobileCart.hidden = cart.item_count === 0;
        }
      } catch (error) {
        // Preserve the existing cart count if the request fails.
      } finally {
        this.cartPending = false;
      }
    }

    disconnectedCallback() {
      clearInterval(this.cartTimer);

      this.controller?.abort();
      this.controller = null;

      const dialog = this.querySelector(".rider-dialog");

      if (dialog && typeof dialog.close === "function") {
        dialog.close();
      }
    }
  }

  customElements.define("monticule-homepage", MonticuleHomepage);
})();